/**
 * @file Sesión vencida → a la pantalla de login.
 *
 * POR QUÉ EXISTE: `RequireAuth` valida la sesión UNA sola vez, al montar, y su
 * propio comentario dice «si expira, la siguiente request retornará 401 y el
 * handler redirige a /login». **Ese handler nunca se construyó.** Había 30+
 * helpers de fetch, cada uno traduciendo el 401 a su mensaje local, y ninguno
 * redirigía: al operador le quedaba la pantalla puesta con un error en inglés
 * («Not authenticated») y un botón «Reintentar» que reintenta contra una sesión
 * muerta, para siempre.
 *
 * Con esa cantidad de puertas, parchar helper por helper garantiza que la
 * próxima que alguien escriba quede afuera. Se intercepta `fetch` UNA vez.
 *
 * CUATRO CASOS donde redirigir automáticamente haría daño, y por eso se excluyen:
 *
 *  1. **Las rutas de autenticación.** Un 401 de `/auth/login` significa «clave
 *     equivocada» y tiene que verse en el formulario. Redirigir ahí es un bucle.
 *  2. **`/auth/me`.** Es la pregunta «¿tengo sesión?»; su 401 es una respuesta
 *     legítima y `RequireAuth` ya sabe qué hacer con ella.
 *  3. **Estando ya en el login.** No hay a dónde ir, y borraría el `from`.
 *  4. **Peticiones a otro dominio.** Un 401 de un tercero no dice nada sobre
 *     nuestra sesión.
 *
 * Y una guarda más: al vencer la sesión suelen fallar VARIAS peticiones a la
 * vez (la pantalla pide productos, sesión y permisos). Sin el cerrojo de
 * `yendoAlLogin`, cada una dispararía su propia navegación.
 */
import { apiBase } from "./env";

/** Rutas cuyo 401 es una respuesta válida, no una sesión vencida. */
const RUTAS_DE_AUTENTICACION = [
  "/auth/login",
  "/auth/me",
  "/auth/otp/",
  "/auth/logout",
  "/auth/password-reset/",
];

/** Una sola navegación aunque fallen diez peticiones juntas. */
let yendoAlLogin = false;

function esDeNuestraApi(url: string): boolean {
  const base = apiBase();
  if (!base) return false;
  try {
    return new URL(url, globalThis.location.href).origin === new URL(base, globalThis.location.href).origin;
  } catch {
    return false;
  }
}

function esRutaDeAutenticacion(url: string): boolean {
  try {
    const { pathname } = new URL(url, globalThis.location.href);
    return RUTAS_DE_AUTENTICACION.some((r) => pathname.includes(r));
  } catch {
    return false;
  }
}

/** Dónde estaba el usuario, para devolverlo ahí después de entrar. */
function rutaActual(): string {
  const { pathname, search, hash } = globalThis.location;
  return `${pathname}${search}${hash}`;
}

/**
 * Manda al login conservando a dónde quería ir.
 *
 * Se usa `location.assign` y no el router: esto puede dispararse desde
 * cualquier punto —incluido un fetch fuera de un componente— donde no hay
 * acceso a `navigate`. Y una recarga completa tiene una ventaja acá: bota
 * cualquier estado en memoria que haya quedado de la sesión anterior.
 */
export function irAlLoginPorSesionVencida(): void {
  if (yendoAlLogin) return;
  const enLogin = globalThis.location.pathname.startsWith("/login");
  if (enLogin) return;
  yendoAlLogin = true;
  const from = encodeURIComponent(rutaActual());
  // `reason=` es la convención que ya usa el cierre por inactividad; se reusa
  // en vez de inventar otro parámetro. `from` va por query y no por el state
  // del router porque `location.assign` no puede llevar state.
  globalThis.location.assign(`/login?reason=expired&from=${from}`);
}

/**
 * Instala el interceptor. Se llama una vez al arrancar la app.
 *
 * Envuelve `fetch` en vez de tocar los 30+ helpers: así cubre también los
 * `fetch` sueltos que hay dentro de algunos componentes, y cualquiera que se
 * escriba mañana.
 */
export function instalarRedireccionPorSesionVencida(): void {
  const original = globalThis.fetch;
  if ((original as { __sesionInstalada?: boolean }).__sesionInstalada) return;

  const envuelto: typeof fetch = async (input, init) => {
    const res = await original(input, init);
    if (res.status !== 401) return res;

    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.href
          : input.url;

    if (esDeNuestraApi(url) && !esRutaDeAutenticacion(url)) {
      irAlLoginPorSesionVencida();
    }
    // La respuesta se devuelve intacta: quien llamó decide qué hacer mientras
    // el navegador despacha la salida. Tragársela lo dejaría esperando.
    return res;
  };

  (envuelto as { __sesionInstalada?: boolean }).__sesionInstalada = true;
  globalThis.fetch = envuelto;
}
